"use client";

import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEventHandler, type ReactNode, type Ref } from "react";
import { createPortal } from "react-dom";
import { isTopLayer, registerLayer } from "./layer-stack";

export type PopoverTriggerProps = {
  id: string;
  onClick: () => void;
  "aria-expanded": boolean;
  "aria-controls": string;
  "aria-haspopup"?: "dialog" | "menu";
  "data-fz-popover-trigger": "true";
};

export type PopoverProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: (props: PopoverTriggerProps) => ReactNode;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  contentRole?: "dialog" | "menu";
  contentAriaLabel?: string;
  contentRef?: Ref<HTMLDivElement>;
  onContentKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  placement?: "right-start";
  anchorPoint?: { x: number; y: number };
  anchorSelector?: string;
  sideOffset?: number;
  portal?: boolean;
};

type CaixaFlutuante = Pick<DOMRect, "left" | "right" | "top" | "width" | "height">;

export function calcularPosicaoPopoverLateral(gatilho: CaixaFlutuante, conteudo: CaixaFlutuante, larguraViewport: number, alturaViewport: number, espaco = 8) {
  const margem = 8;
  let left = gatilho.right + espaco;
  if (left + conteudo.width > larguraViewport - margem) left = gatilho.left - espaco - conteudo.width;
  left = Math.min(Math.max(margem, left), Math.max(margem, larguraViewport - conteudo.width - margem));
  const top = Math.min(Math.max(margem, gatilho.top), Math.max(margem, alturaViewport - conteudo.height - margem));
  return { left, top };
}

export function Popover({
  open,
  onOpenChange,
  trigger,
  children,
  className = "",
  contentClassName = "",
  contentRole,
  contentAriaLabel,
  contentRef,
  onContentKeyDown,
  placement,
  anchorPoint,
  anchorSelector,
  sideOffset = 8,
  portal = false,
}: PopoverProps) {
  const baseId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const previouslyOpen = useRef(open);
  const onOpenChangeRef = useRef(onOpenChange);
  const triggerId = `${baseId}-trigger`;
  const contentId = `${baseId}-content`;
  const layerId = `${baseId}-layer`;
  const [floatingStyle, setFloatingStyle] = useState<CSSProperties>();

  useLayoutEffect(() => {
    if (!open || placement !== "right-start") {
      setFloatingStyle(undefined);
      return;
    }

    function posicionar() {
      const trigger = rootRef.current?.querySelector<HTMLElement>("[data-fz-popover-trigger=\"true\"]");
      const gatilho: CaixaFlutuante | undefined = anchorPoint
        ? { left: anchorPoint.x, right: anchorPoint.x, top: anchorPoint.y, width: 0, height: 0 }
        : (anchorSelector ? trigger?.closest<HTMLElement>(anchorSelector) : trigger)?.getBoundingClientRect();
      const conteudo = document.getElementById(contentId)?.getBoundingClientRect();
      if (!gatilho || !conteudo) return;

      const { left, top } = calcularPosicaoPopoverLateral(gatilho, conteudo, window.innerWidth, window.innerHeight, sideOffset);

      setFloatingStyle({ position: "fixed", left, top, maxWidth: "calc(100vw - 1rem)", maxHeight: "calc(100dvh - 1rem)", overflowY: "auto", visibility: "visible" });
    }

    posicionar();
    window.addEventListener("resize", posicionar);
    window.addEventListener("scroll", posicionar, true);
    return () => {
      window.removeEventListener("resize", posicionar);
      window.removeEventListener("scroll", posicionar, true);
    };
  }, [anchorPoint?.x, anchorPoint?.y, anchorSelector, contentId, open, placement, sideOffset]);

  useEffect(() => {
    onOpenChangeRef.current = onOpenChange;
  }, [onOpenChange]);

  useEffect(() => {
    if (!open) return;
    return registerLayer(layerId);
  }, [layerId, open]);

  useEffect(() => {
    if (!open) return;

    function fecharAoClicarFora(event: PointerEvent) {
      if (!isTopLayer(layerId)) return;
      const alvo = event.target as Node;
      if (!rootRef.current?.contains(alvo) && !document.getElementById?.(contentId)?.contains(alvo)) onOpenChangeRef.current(false);
    }

    function fecharAoPressionarEscape(event: KeyboardEvent) {
      if (event.key !== "Escape" || !isTopLayer(layerId)) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      onOpenChangeRef.current(false);
    }

    document.addEventListener("pointerdown", fecharAoClicarFora);
    document.addEventListener("keydown", fecharAoPressionarEscape);
    return () => {
      document.removeEventListener("pointerdown", fecharAoClicarFora);
      document.removeEventListener("keydown", fecharAoPressionarEscape);
    };
  }, [contentId, layerId, open]);

  useEffect(() => {
    if (previouslyOpen.current && !open) {
      rootRef.current?.querySelector<HTMLElement>("[data-fz-popover-trigger=\"true\"]")?.focus();
    }
    previouslyOpen.current = open;
  }, [open]);

  const conteudo = (
    <div
      ref={contentRef}
      id={contentId}
      role={contentRole}
      aria-label={contentAriaLabel}
      data-fz-floating="popover"
      hidden={!open}
      style={placement === "right-start" ? floatingStyle ?? (portal || anchorPoint || anchorSelector ? {
        position: "fixed",
        left: 0,
        top: 0,
        maxWidth: "calc(100vw - 1rem)",
        maxHeight: "calc(100dvh - 1rem)",
        overflowY: "auto",
        visibility: "hidden",
      } : undefined) : undefined}
      className={`border border-[var(--fz-floating-border)] bg-[var(--fz-floating-bg)] text-[var(--fz-floating-text)] shadow-[var(--fz-floating-shadow)] ${contentClassName}`}
      onKeyDown={onContentKeyDown}
    >
      {open ? children : null}
    </div>
  );

  return (
    <div ref={rootRef} className={`${portal && anchorPoint ? "fixed left-0 top-0 h-0 w-0" : "relative"} ${className}`}>
      {trigger({
        id: triggerId,
        onClick: () => onOpenChange(!open),
        "aria-expanded": open,
        "aria-controls": contentId,
        "aria-haspopup": contentRole,
        "data-fz-popover-trigger": "true",
      })}
      {portal ? (open && typeof document !== "undefined" ? createPortal(conteudo, document.body) : null) : conteudo}
    </div>
  );
}
